import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Caption, LabelS, Text16 } from '@sorare/core/src/atoms/typography';
import { Bonus } from '@sorare/core/src/components/collections/Bonus';
import { CardsNumber } from '@sorare/core/src/components/collections/CardsNumber';
import { Ranking } from '@sorare/core/src/components/collections/Ranking';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import CollectionBackground from 'components/collections/CollectionBackground';

import { CollectionPreview_cardCollection } from './__generated__/index.graphql';

const StyledCollectionBackground = styled(CollectionBackground)`
  padding: var(--intermediate-unit) var(--intermediate-unit) 0;
`;

const Content = styled(Vertical)`
  height: 100%;
  border-radius: var(--double-unit);
  background: var(--collection-bottom-background, var(--c-static-neutral-900));
  overflow: hidden;
`;

const Stats = styled.div`
  padding: 0 var(--intermediate-unit) var(--intermediate-unit);
`;

const StyledLink = styled(Link)`
  transition: transform 0.2s ease-in-out;
  &:hover,
  &:focus {
    color: inherit;
    transform: scale(1.02);
  }
`;

const ScarcityWrapper = styled.div`
  align-self: flex-start;
`;

const StyledCaption = styled(Caption)`
  align-self: flex-start;
`;

const ImgWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 80px;
  margin-bottom: var(--unit);
`;

const StyledImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;

const StyledText16 = styled(Text16)`
  margin: var(--intermediate-unit) 0;
  text-align: center;
`;

const StatsWrapper = styled(Vertical).attrs({ gap: 0 })`
  align-self: stretch;
  margin-top: auto;
`;

const StatsLine = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTag = styled(LabelS).attrs({ as: 'div', bold: true })`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  display: flex;
  align-items: center;
  padding: var(--half-unit) var(--unit);
  color: var(--c-score-high);
  background-color: rgba(var(--c-rgb-green-300), 0.16);
  border-radius: var(--half-unit);
  box-shadow: var(--shadow-300);
`;

const CollectionPreviewStats = ({
  userCardCollection,
  slotsCount,
}: {
  userCardCollection: CollectionPreview_cardCollection['userCardCollection'];
  slotsCount: number;
}) => {
  const defaults = {
    fulfilledSlotsCount: 0,
    score: 0,
    bonus: 0,
    liveRanking: 0,
  };
  const { fulfilledSlotsCount, score, bonus, liveRanking } =
    userCardCollection || defaults;

  return (
    <StatsWrapper>
      <StatsLine>
        <FormattedMessage {...glossary.cards} />
        <CardsNumber ownedCards={fulfilledSlotsCount} totalCards={slotsCount} />
      </StatsLine>
      <StatsLine>
        <FormattedMessage {...fantasy.rank} />
        <Ranking liveRanking={liveRanking} />
      </StatsLine>
      <StatsLine>
        <FormattedMessage {...fantasy.score} />
        <Score score={score} />
      </StatsLine>
      <StatsLine>
        <FormattedMessage {...fantasy.bonus} />
        <Bonus bonus={bonus} />
      </StatsLine>
    </StatsWrapper>
  );
};

const Wrapper = ({ children, to }: { children: ReactNode; to?: string }) => {
  if (to) {
    return <StyledLink to={to}>{children}</StyledLink>;
  }

  return children;
};

type Props = {
  collection: CollectionPreview_cardCollection;
  to?: string;
};

export const CollectionPreview = ({ collection, to }: Props) => {
  const {
    bannerPictureUrl,
    rarity,
    team,
    season,
    slotsCount,
    userCardCollection,
  } = collection;
  if (!team) return null;

  const completed = userCardCollection?.fulfilledSlotsCount === slotsCount;

  const { pictureUrl, name } = team;

  return (
    <Wrapper to={to}>
      <Content>
        <StyledCollectionBackground
          fullBanner
          className={classNames({ completed })}
          bannerPictureUrl={bannerPictureUrl}
          colored={completed}
        >
          <ScarcityWrapper>
            <Caption>
              <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
            </Caption>
          </ScarcityWrapper>
          <StyledCaption color="rgba(var(--c-rgb-neutral-1000), 0.8)" bold>
            {season?.name}
          </StyledCaption>

          {completed && (
            <StyledTag>
              <FormattedMessage {...glossary.completed} />
            </StyledTag>
          )}
          <ImgWrapper>
            {pictureUrl && <StyledImg alt="" src={pictureUrl} />}
          </ImgWrapper>
          <StyledText16>{name}</StyledText16>
        </StyledCollectionBackground>
        <Stats>
          <CollectionPreviewStats
            slotsCount={slotsCount}
            userCardCollection={userCardCollection}
          />
        </Stats>
      </Content>
    </Wrapper>
  );
};

CollectionPreview.fragments = {
  cardCollection: gql`
    fragment CollectionPreview_cardCollection on CardCollection {
      slug
      bannerPictureUrl
      rarity
      slotsCount
      team {
        ... on TeamInterface {
          slug
          name
          pictureUrl
        }
      }
      season {
        startYear
        name
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        score
        bonus
        liveRanking
      }
    }
  ` as TypedDocumentNode<CollectionPreview_cardCollection>,
};
